import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DriverShell } from "@/components/shells";
import { Panel, Spinner, Btn, Field, Input, Textarea, Select } from "@/components/kit";
import { AddressLookup } from "@/components/AddressLookup";
import { useAsync } from "@/lib/hooks";
import { loadDriverContext, createBooking } from "@/lib/driverData";
import { useDraftPersistence, clearDraft } from "@/lib/useDraftPersistence";
import { TYRE_POSITIONS, TYRE_ISSUES } from "@/lib/vda";

export default function DriverTyres() {
  const nav = useNavigate();
  const { data: ctx, loading } = useAsync(() => loadDriverContext(), []);
  const [form, setForm] = useState({
    positions: [], issue: "puncture", safe_to_drive: "yes", mobile_fitting: false,
    tyre_size: "", load_rating: "", speed_rating: "",
    description: "", address: {},
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const togglePos = (p) =>
    setForm((f) => ({ ...f, positions: f.positions.includes(p) ? f.positions.filter((x) => x !== p) : [...f.positions, p] }));

  const draftScope = ctx?.vehicle?.id ? `tyres_${ctx.vehicle.id}` : null;
  useDraftPersistence({ scope: draftScope, form, setForm, enabled: !!draftScope, onRestore: () => toast.info("Restored your previous progress") });

  if (loading) return <DriverShell back="/driver"><Spinner /></DriverShell>;

  const submit = async (e) => {
    e.preventDefault();
    if (form.positions.length === 0) { toast.error("Please select at least one tyre position."); return; }
    if (!form.tyre_size.trim() || !form.load_rating.trim() || !form.speed_rating.trim()) {
      toast.error("Tyre size, load rating and speed rating are all required — check the sidewall.");
      return;
    }
    setSaving(true);
    try {
      const details = {
        flow: "tyres",
        positions: form.positions,
        position_labels: form.positions.map((p) => TYRE_POSITIONS.find((t) => t.value === p)?.label).filter(Boolean),
        issue: form.issue,
        issue_label: TYRE_ISSUES.find((i) => i.value === form.issue)?.label,
        mobile_fitting: form.mobile_fitting,
        tyre_size: form.tyre_size.trim(),
        load_rating: form.load_rating.trim(),
        speed_rating: form.speed_rating.trim().toUpperCase(),
      };
      if (form.address?.address_text) details.location = form.address;
      const id = await createBooking(ctx, {
        request_type: "tyres", request_category: "tyres", request_details: details,
        safe_to_drive: form.safe_to_drive === "yes",
        description: form.description || null,
        location_text: form.address?.address_text || null, postcode: form.address?.postcode || null,
        priority: form.safe_to_drive === "no" ? "high" : "normal",
      });
      clearDraft(draftScope);
      toast.success("Tyre request submitted and routed to your Venson team.");
      nav(`/driver/booking/${id}`, { replace: true });
    } catch (err) {
      toast.error(err.message || "We couldn't submit your request. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <DriverShell back="/driver" title="Tyres">
      <form onSubmit={submit} className="space-y-4" data-testid="tyres-form">
        <div>
          <span className="mb-1.5 block vda-tick text-muted-foreground">Which tyres? (tap all that apply)</span>
          <div className="grid grid-cols-2 gap-2" data-testid="tyre-positions">
            {TYRE_POSITIONS.map((p) => {
              const on = form.positions.includes(p.value);
              return (
                <button key={p.value} type="button" data-testid={`tyre-pos-${p.value}`} onClick={() => togglePos(p.value)}
                  className={`h-16 rounded-[6px] border text-left px-3 transition-colors ${on ? "border-primary bg-primary/10 text-primary" : "border-[hsl(var(--input))]"}`}>
                  <div className="text-sm font-semibold">{p.label}</div>
                  <div className="text-[11px] text-muted-foreground">{p.hint}</div>
                </button>
              );
            })}
          </div>
        </div>
        <Field label="What's wrong?">
          <Select data-testid="tyre-issue" value={form.issue} onChange={(e) => set("issue", e.target.value)}>
            {TYRE_ISSUES.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
          </Select>
        </Field>

        <Panel className="p-3 bg-secondary/40" data-testid="tyre-sidewall-help">
          <span className="vda-tick text-muted-foreground">Read it off the sidewall — all three are required</span>
          <div className="mt-2 flex items-center gap-2 text-sm">
            <span className="vda-reg font-semibold tracking-wide">205/65 R16C</span>
            <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary text-xs font-semibold">107/105T</span>
          </div>
          <p className="mt-1.5 text-[11px] text-muted-foreground"><strong>205/65 R16C</strong> = size · <strong>107/105</strong> = load rating · <strong>T</strong> = speed rating (e.g. 96<strong>H</strong>).</p>
        </Panel>
        <Field label="Tyre size (required)">
          <Input data-testid="tyre-size" value={form.tyre_size} onChange={(e) => set("tyre_size", e.target.value.toUpperCase())} placeholder="e.g. 205/65 R16C" className="vda-reg uppercase" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Load rating (required)">
            <Input data-testid="tyre-load" value={form.load_rating} onChange={(e) => set("load_rating", e.target.value)} placeholder="e.g. 107/105 or 96" />
          </Field>
          <Field label="Speed rating (required)">
            <Input data-testid="tyre-speed" value={form.speed_rating} onChange={(e) => set("speed_rating", e.target.value.toUpperCase().slice(0, 3))} placeholder="e.g. T or H" className="uppercase" />
          </Field>
        </div>

        <Field label="Is the vehicle safe to drive?">
          <div className="grid grid-cols-2 gap-3">
            {["yes", "no"].map((v) => (
              <button key={v} type="button" data-testid={`tyre-safe-${v}`} onClick={() => set("safe_to_drive", v)}
                className={`h-11 rounded-[6px] border text-sm font-medium ${form.safe_to_drive === v ? (v === "no" ? "border-[hsl(var(--vda-red))] bg-[hsl(var(--vda-red))]/10 text-[hsl(var(--vda-red))]" : "border-primary bg-primary/10 text-primary") : "border-[hsl(var(--input))]"}`}>
                {v === "yes" ? "Yes, safe" : "No, not safe"}
              </button>
            ))}
          </div>
        </Field>
        {form.safe_to_drive === "no" && (
          <Panel className="p-3 text-sm bg-[hsl(var(--vda-red))]/8 border-[hsl(var(--vda-red))]/30">
            If the vehicle is unsafe, do not drive it. Use the <strong>Emergency</strong> tab to call for assistance.
          </Panel>
        )}
        <label className="vda-panel p-3 flex items-center gap-2 cursor-pointer">
          <input type="checkbox" data-testid="tyre-mobile" checked={form.mobile_fitting} onChange={(e) => set("mobile_fitting", e.target.checked)} />
          <span className="text-sm">Request mobile fitting where the vehicle is</span>
        </label>
        <AddressLookup label="Where is the vehicle?" value={form.address} onChange={(a) => set("address", a)} testIdPrefix="tyre-address" />
        <Field label="Anything else?">
          <Textarea data-testid="tyre-description" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Tyre size, how it happened, etc." />
        </Field>
        <Btn type="submit" loading={saving} data-testid="tyres-submit" className="w-full">Submit request</Btn>
      </form>
    </DriverShell>
  );
}
